import { DashboardConfig, DashboardPanel } from "@soft-db/monitoring-api-client";
import { confirm } from "@soft-db/ui";
import * as _ from "lodash-es";
import { v4 as uuidv4 } from "uuid";
import { useScopeDetails } from "../../api/current-user";
import { createRestrictiveInjectionState } from "../../composable/restrictive-injection-state";
import { useScopeStore } from "../scope";
import { DashboardLayoutType, useDashboardLayout } from "./dashboard-layout";
import { useDashboardState } from "./dashboard-state";

function defaultDashboardConfig(): DashboardConfig {
	return {
		vibrationCriterias: {
			exceedance: "",
			warning: "",
		},
	};
}

export const [provideDashboardEditor, useDashboardEditor] = createRestrictiveInjectionState(() => {
	const state = useDashboardState();

	const layouts = useDashboardLayout();
	const scopeStore = useScopeStore();

	const isEditing = ref(false);

	watch(state.view, () => {
		if (state.view.value.type === "edit" && !isEditing.value) {
			startEdit();
		}
	});

	const configs = ref<DashboardConfig>(defaultDashboardConfig());
	const panels = ref<DashboardPanel[]>([]);

	const hasPendingChanges = computed(() => {
		if (!isEditing.value) {
			return false;
		}

		return (
			!_.isEqual(panels.value, layouts.currentLayout.value?.panels) ||
			!_.isEqual(configs.value, layouts.currentLayout.value?.config)
		);
	});

	function getPanelIdx(panels: DashboardPanel[], id: string): number | null {
		const idx = panels.findIndex((p) => p.id === id);
		if (idx === -1) {
			return null;
		}

		return idx;
	}

	function getPanel(panels: DashboardPanel[], id: string): DashboardPanel | null {
		const idx = getPanelIdx(panels, id);
		if (idx == null) {
			return null;
		}

		return panels[idx];
	}

	function panelChanged(id: string) {
		if (!isEditing.value) {
			return false;
		}

		const original = getPanel(layouts.currentLayout.value?.panels || [], id);
		const current = getPanel(panels.value, id);

		return !_.isEqual(original, current);
	}

	function startEdit() {
		if (isEditing.value || layouts.currentLayout.value == null) {
			return;
		}

		panels.value = structuredClone(layouts.currentLayout.value.panels);

		panels.value = panels.value.map((p) => reactive(p));

		configs.value = structuredClone(layouts.currentLayout.value.config);
		isEditing.value = true;
	}

	async function writeEdit() {
		await layouts.updateCurrentLayout({
			panels: panels.value,
			config: configs.value,
		});

		isEditing.value = false;
		panels.value = [];
		configs.value = defaultDashboardConfig();
	}

	const { scope: scopeDetails } = useScopeDetails(() => scopeStore.scope);

	async function confirmLayoutSave(layout: DashboardLayoutType | null | undefined) {
		if (layout === "default" || layout === "space") {
			let scope: string = "";

			if (scopeDetails.value != null && "details" in scopeDetails.value) {
				scope = scopeDetails.value.details.name;
			}

			const confirmed = await confirm.yesNo(t("dashboard.saveDefaultTitle"), {
				description: t("dashboard.saveDefaultDescription", {
					scope,
				}),
			});
			return confirmed;
		}

		return true;
	}

	const redirectSaveType = computed<DashboardLayoutType | null>(() => {
		const { scope } = scopeStore;

		const scopeType = scope.type;
		const selectedLayout = layouts.selectedLayout.value;

		const canCreateDefault = layouts.canCreateDefault.value;
		const canCreatePublic = layouts.canCreatePublic.value;

		const isGlobalDefault = selectedLayout === "default";
		const isSpaceDefault = selectedLayout === "space";

		const isAnyDefault = isGlobalDefault || isSpaceDefault;

		const isPublicPage = state.context.value?.publicPage != null;

		if (isPublicPage) {
			return isAnyDefault && canCreatePublic ? "public_page" : null;
		}

		if (scopeType === "distributor" && isGlobalDefault && !canCreateDefault) {
			return "user";
		}

		if (scopeType === "space") {
			if (isGlobalDefault && canCreateDefault) {
				return "space";
			}

			if (isAnyDefault && !canCreateDefault) {
				return "user";
			}
		}

		return null;
	});

	async function saveEdit() {
		if (!isEditing.value || layouts.currentLayout.value == null) {
			return;
		}

		const editableLayouts = layouts.editableLayoutsTypes.value;
		const selectedLayout = layouts.selectedLayout.value;

		if (editableLayouts.length === 0) {
			return;
		}

		if (editableLayouts.length === 1 && editableLayouts[0] !== selectedLayout) {
			return saveAs(editableLayouts[0]);
		}

		const currentLayoutType = layouts.getLayoutTypeFromScope(layouts.currentLayout.value.scope);
		const confirmed = await confirmLayoutSave(currentLayoutType);
		if (!confirmed) {
			return;
		}

		await writeEdit();
	}

	async function saveAs(layout: DashboardLayoutType) {
		if (!isEditing.value) {
			return;
		}

		const confirmed = await confirmLayoutSave(layout);
		if (!confirmed) {
			return;
		}

		if (layouts.hasLayoutType(layout)) {
			if (layout === "user") {
				const confirmed = await confirm.yesNo(t("dashboard.overwriteConfirm"));
				if (!confirmed) {
					return;
				}
			}

			layouts.changeLayout(layout);
		} else {
			switch (layout) {
				case "default":
				case "space":
					await layouts.createDefaultLayout();
					break;

				case "user":
					await layouts.createUserLayout();
					break;

				case "public_page":
					await layouts.createPublicLayout();
					break;
			}
		}

		panels.value = panels.value.map((panel) => ({
			...panel,
			id: uuidv4(),
		}));

		await writeEdit();
	}

	function createEmptyPanel(): DashboardPanel {
		return {
			id: "",
			position: {
				x: null,
				y: null,
				width: 12,
				height: 8,
			},
			title: "",
			plugin: {
				version: 1,
				type: "",
				options: undefined,
				data: undefined,
			},
		};
	}

	function addPanel() {
		if (!isEditing.value) {
			return;
		}

		const id = uuidv4();
		const panel = createEmptyPanel();
		panel.id = id;

		panels.value = [...panels.value, panel];
	}

	async function resetPanel(id: string) {
		if (!isEditing.value || layouts.currentLayout.value == null || !panelChanged(id)) {
			return;
		}

		if (!(await confirm.yesNo(t("common.cancelChangeConfirm")))) {
			return;
		}

		let originalPanel = getPanel(layouts.currentLayout.value.panels, id);
		if (originalPanel == null) {
			// Panel not yet saved, reset it to default.
			originalPanel = createEmptyPanel();
			originalPanel.id = id;
		}

		originalPanel = structuredClone(originalPanel);

		const idx = getPanelIdx(panels.value, id);
		if (idx == null) {
			return;
		}

		const newPanels = [...panels.value];
		newPanels[idx] = originalPanel;
		panels.value = newPanels;
	}

	const { t } = useI18n();

	async function removePanel(id: string) {
		if (!isEditing.value) {
			return;
		}

		if (!(await confirm.yesNo(t("dashboard.removePlugin")))) {
			return;
		}

		const idx = getPanelIdx(panels.value, id);
		if (idx == null) {
			return;
		}

		const newPanels = [...panels.value];
		newPanels.splice(idx, 1);
		panels.value = newPanels;
	}

	async function updatePanel(id: string, panel: DashboardPanel) {
		if (!isEditing.value) {
			return;
		}

		const idx = getPanelIdx(panels.value, id);
		if (idx == null) {
			return;
		}

		panels.value = [...panels.value];
		panels.value[idx] = panel;
	}

	async function cancelEdit() {
		if (!isEditing.value) {
			return;
		}

		if (hasPendingChanges.value) {
			if (!(await confirm.yesNo(t("dashboard.forgetChangeConfirm")))) {
				return;
			}
		}

		isEditing.value = false;
		panels.value = [];
		configs.value = defaultDashboardConfig();
	}

	return {
		isEditing: readonly(isEditing),
		redirectSaveType,
		panels,
		configs,
		hasPendingChanges,
		panelChanged,
		startEdit,
		saveAs,
		addPanel,
		removePanel,
		saveEdit,
		updatePanel,
		resetPanel,
		cancelEdit,
	};
});
